import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";

import Widget from "../../components/Widget";
import WidgetDateRange from "../../components/WidgetDateRange";
import DashboardEmptyState from "./DashboardEmptyState";
import WidgetHelpLink from "./WidgetHelpLink";
import LegendWithSelection from "./LegendWithSelection";
import WidgetTotalsItem from "./WidgetTotalsItem";

import reportApi from "../../api/reportApi";

import { convertDateForChart, formatTrendText, getDaysDiff, transformDataDateForDemo } from "./dashboardHelper";
import planService from "../../services/planService";
import { PLAN_TYPE_ENUM } from "../../constants/planConstants";
import { getMessage } from "../../messages";
import { POINTS_ANALYTICS_HELP_LINK } from "../../constants/dashboardConstants";

const CHART_HEIGHT = 164;

const CHART_COLORS = {
  ACCUMULATED: "#86D985",
  REDEEMED: "#52B9DE",
  PENDING: "#fcb15a",
  EXPIRED: "#d86761"
};

const CHART_META_DATA_POINTS = [
  {
    id: "pointsToSpendConfirmed",
    displayName: getMessage("pointsAnalyticsWidget.chartMetadata.points.accumulated.displayName"),
    color: CHART_COLORS.ACCUMULATED,
    selectedInLegend: true,
    totalsTooltip: getMessage("pointsAnalyticsWidget.chartMetadata.points.accumulated.totalsTooltip")
  },
  {
    id: "pointsToSpendRedeemed",
    displayName: getMessage("pointsAnalyticsWidget.chartMetadata.points.redeemed.displayName"),
    color: CHART_COLORS.REDEEMED,
    selectedInLegend: true,
    totalsTooltip: getMessage("pointsAnalyticsWidget.chartMetadata.points.redeemed.totalsTooltip")
  },
  {
    id: "pointsToSpendPending",
    displayName: getMessage("pointsAnalyticsWidget.chartMetadata.points.pending.displayName"),
    color: CHART_COLORS.PENDING,
    totalsTooltip: getMessage("pointsAnalyticsWidget.chartMetadata.points.pending.totalsTooltip")
  },
  {
    id: "pointsToSpendExpired",
    displayName: getMessage("pointsAnalyticsWidget.chartMetadata.points.expired.displayName"),
    color: CHART_COLORS.EXPIRED,
    totalsTooltip: getMessage("pointsAnalyticsWidget.chartMetadata.points.expired.totalsTooltip")
  }
];

const CHART_META_DATA_POINTS_AND_BENEFITS = [
  {
    id: "statusConfirmed",
    displayName: getMessage("pointsAnalyticsWidget.chartMetadata.points.accumulated.displayName"),
    color: CHART_COLORS.ACCUMULATED,
    selectedInLegend: true,
    totalsTooltip: getMessage("pointsAnalyticsWidget.chartMetadata.points.accumulated.totalsTooltip")
  },
  {
    id: "statusPending",
    displayName: getMessage("pointsAnalyticsWidget.chartMetadata.points.pending.displayName"),
    color: CHART_COLORS.PENDING,
    totalsTooltip: getMessage("pointsAnalyticsWidget.chartMetadata.points.pending.totalsTooltip")
  },
  {
    id: "statusExpired",
    displayName: getMessage("pointsAnalyticsWidget.chartMetadata.points.expired.displayName"),
    color: CHART_COLORS.EXPIRED,
    totalsTooltip: getMessage("pointsAnalyticsWidget.chartMetadata.points.expired.totalsTooltip")
  }
];

const CHART_META_DATA_SPEND = [
  {
    id: "statusConfirmed",
    displayName: getMessage("pointsAnalyticsWidget.chartMetadata.spend.accumulated.displayName"),
    color: CHART_COLORS.ACCUMULATED,
    selectedInLegend: true,
    totalsTooltip: getMessage("pointsAnalyticsWidget.chartMetadata.spend.accumulated.totalsTooltip")
  },
  {
    id: "cashbackConfirmed",
    displayName: getMessage("pointsAnalyticsWidget.chartMetadata.spend.accumulatedBashback.displayName"),
    color: CHART_COLORS.REDEEMED,
    selectedInLegend: true,
    totalsTooltip: getMessage("pointsAnalyticsWidget.chartMetadata.spend.accumulatedBashback.totalsTooltip")
  },
  {
    id: "statusPending",
    displayName: getMessage("pointsAnalyticsWidget.chartMetadata.spend.pending.displayName"),
    color: CHART_COLORS.PENDING,
    totalsTooltip: getMessage("pointsAnalyticsWidget.chartMetadata.spend.pending.totalsTooltip")
  },
  {
    id: "statusExpired",
    displayName: getMessage("pointsAnalyticsWidget.chartMetadata.spend.expired.displayName"),
    color: CHART_COLORS.EXPIRED,
    totalsTooltip: getMessage("pointsAnalyticsWidget.chartMetadata.spend.expired.totalsTooltip")
  }
];

class PointsAnalyticsWidget extends Component {
  constructor(props) {
    super(props);

    let legendSelection = {};
    let chartSeriesArr = [];

    const isSpendPlan = planService.isSpendPlan(props.plan);

    switch (props.plan.planType) {
      case PLAN_TYPE_ENUM.POINTS:
        this.chartMetaData = CHART_META_DATA_POINTS;
        break;
      case PLAN_TYPE_ENUM.POINTS_AND_BENEFITS:
        this.chartMetaData = CHART_META_DATA_POINTS_AND_BENEFITS;
        break;
      case PLAN_TYPE_ENUM.SPEND:
        this.chartMetaData = CHART_META_DATA_SPEND;
        break;
    }

    this.chartMetaData.forEach((meta) => {
      legendSelection[meta.id] = !!meta.selectedInLegend;
      chartSeriesArr.push({ id: meta.id, name: meta.displayName, color: meta.color, data: [] });
    });

    this.state = {
      loading: true,
      selectedDaysRange: getDaysDiff(props.initialStartDate, props.initialEndDate),
      dateRange: {
        startDateStr: props.initialStartDate,
        endDateStr: props.initialEndDate
      },
      chartSeriesArr,
      totals: {},
      legendSelection,
      showEmptyState: false,
      isSpendPlan
    };
  }

  componentDidMount() {
    this.loadData(this.state.dateRange);
  }

  loadData(dateRange) {
    const promises = [reportApi.getPointsChartData(dateRange), reportApi.getPointsChartTotals(dateRange)];

    this.setState({ loading: true, showEmptyState: false });

    Promise.all(promises)
      .then((response) => {
        this.setChartSeriesState(response[0].data);
        this.setTotalsState(response[1]);
      })
      .catch((error) => {
        this.setState({ showEmptyState: true });
        throw error;
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  }

  getNumberValueAttr() {
    return { humanize: "auto", precision: "2", "trim-fraction-zeros": "true" };
  }

  getPctValueAttr() {
    return { humanize: "auto", precision: "1", type: "percent", "trim-fraction-zeros": "true" };
  }

  getTrendText(value, daysRange) {
    if (this.chartMetaData.length > 3) {
      return formatTrendText`${value} than <br /> previous ${daysRange} days`;
    }
    return formatTrendText`${value} than previous ${daysRange} days`;
  }

  getItemsForLegend() {
    return this.chartMetaData.map((meta) => {
      return {
        id: meta.id,
        displayName: meta.displayName,
        color: meta.color,
        isSelected: this.state.legendSelection[meta.id]
      };
    });
  }

  setChartSeriesState(dataRows) {
    let { chartSeriesArr } = { ...this.state };

    if (!dataRows) {
      this.setState({ showEmptyState: true });
      return;
    }

    dataRows = transformDataDateForDemo(dataRows, this.state.selectedDaysRange);
    chartSeriesArr.forEach((series) => {
      series.data = [];
    });

    dataRows.forEach((row) => {
      const date = convertDateForChart(row.date);

      chartSeriesArr.forEach((series) => {
        series.data.push({
          x: date,
          y: row[series.id] || 0
        });
      });
    });

    this.setState({ chartSeriesArr });
  }

  setTotalsState(data) {
    this.setState({ totals: data });
  }

  onTimeRangeChange(value) {
    const { start, end } = value;
    let dateRange = {
      startDateStr: start,
      endDateStr: end
    };
    this.setState({ dateRange, selectedDaysRange: getDaysDiff(start, end) });
    this.loadData(dateRange);
  }

  onLegendSelectionChange(selectionId) {
    let { legendSelection } = { ...this.state };

    legendSelection[selectionId] = !legendSelection[selectionId];

    this.setState({ legendSelection });
  }

  renderTotals() {
    const { selectedDaysRange, totals, loading } = { ...this.state };
    if (loading) {
      return null;
    }

    return (
      <div className="e-grid">
        {this.chartMetaData.map((metaData) => {
          const prevDiffPct = totals[metaData.id]?.prevDiffPct || 0;
          const value = totals[metaData.id]?.value || 0;
          return (
            <div key={metaData.id} className={`e-cell e-cell-${12 / this.chartMetaData.length}`}>
              <WidgetTotalsItem
                title={metaData.displayName}
                titleTooltip={metaData.totalsTooltip}
                value={value}
                valueNumericAttr={this.getNumberValueAttr()}
                valueColor={metaData.color}
                trendValue={prevDiffPct}
                trendText={this.getTrendText(prevDiffPct, selectedDaysRange)}
              />
            </div>
          );
        })}
      </div>
    );
  }

  renderChart() {
    const { chartSeriesArr, legendSelection } = { ...this.state };

    return (
      <ec-chart height={CHART_HEIGHT} domain-type="time">
        {chartSeriesArr.map((series) => {
          let attr = {};

          if (!legendSelection[series.id]) {
            attr["visible"] = false;
            attr["hidden-in-tooltip"] = true;
          }

          return (
            <ec-series-line
              key={series.id}
              name={series.name}
              data={JSON.stringify(series.data)}
              color={series.color}
              format-y=",.0f"
              {...attr}
            ></ec-series-line>
          );
        })}
      </ec-chart>
    );
  }

  renderContent() {
    const { showEmptyState } = { ...this.state };

    if (showEmptyState) {
      return <DashboardEmptyState />;
    }

    return (
      <Fragment>
        <div>{this.renderTotals()}</div>
        <div className="e-margin-top-m">{this.renderChart()}</div>
        <div className="e-margin-top-m">
          <LegendWithSelection
            items={this.getItemsForLegend()}
            visible={true}
            onChange={this.onLegendSelectionChange.bind(this)}
          />
        </div>
      </Fragment>
    );
  }

  render() {
    const { isSpendPlan } = { ...this.state };
    return (
      <Widget
        title={
          isSpendPlan
            ? getMessage("pointsAnalyticsWidget.title.spend")
            : getMessage("pointsAnalyticsWidget.title.points")
        }
        className="points-analytics-widget"
        headerActions={
          <WidgetDateRange
            onChange={this.onTimeRangeChange.bind(this)}
            isDemoMode={false}
            dateRange={{ start: this.state.dateRange.startDateStr, end: this.state.dateRange.endDateStr }}
          />
        }
        footerActions={<WidgetHelpLink linkUrl={POINTS_ANALYTICS_HELP_LINK} />}
        content={this.renderContent()}
        loading={this.state.loading}
        isEmptyState={this.state.showEmptyState}
      />
    );
  }
}

PointsAnalyticsWidget.propTypes = {
  plan: PropTypes.object,
  initialStartDate: PropTypes.string,
  initialEndDate: PropTypes.string
};

export default PointsAnalyticsWidget;
